import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      toast.success('¡Bienvenido!');
      navigate(result?.user?.onboarding_complete ? '/dashboard' : '/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0d] flex">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-14">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold">N</span>
            </div>
            <span className="text-xl font-semibold text-white">Nova</span>
          </Link>

          <h1 className="text-headline text-white mb-2">Bienvenido</h1>
          <p className="text-[#6e6e73] mb-10">Ingresa a tu cuenta para continuar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-caption block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="nova-input"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label className="text-caption block mb-2">Contraseña</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="nova-input pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6e6e73] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full nova-btn nova-btn-primary py-3"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Ingresando...</> : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-10 text-center text-[#6e6e73]">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors" data-testid="register-link">
              Regístrate gratis
            </Link>
          </p>

          <div className="mt-10 p-4 bg-[#161618] border border-white/[0.06] rounded-xl text-center">
            <p className="text-xs text-[#48484d]">Demo: demo@nova.app / demo123</p>
          </div>
        </motion.div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#111113] border-l border-white/[0.04] relative overflow-hidden">
        <div className="absolute inset-0 nova-grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px]" />
        <div className="relative text-center px-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Gestiona tu negocio<br />de forma inteligente
          </h2>
          <p className="text-[#6e6e73] text-lg">
            Clientes, citas, membresías y métricas<br />en una sola plataforma
          </p>
        </div>
      </div>
    </div>
  );
}
